import { useState } from "react";
import { toast } from "sonner";
import { Sparkle, Tag, Monitor } from "@phosphor-icons/react";
import { allPhotos } from "@/components/Thumb";
import { api, formatApiErrorDetail } from "@/lib/api";

const FLAGS = [
  ["new_arrival", "New arrival", Sparkle],
  ["on_sale", "On sale", Tag],
  ["showroom_featured", "Screen", Monitor],
];

// Why a flagged slab still will not appear on the public website.
const blocker = (slab) => {
  if (slab.status && slab.status !== "In Stock")
    return `Hidden on the site — ${slab.status.toLowerCase()}`;
  if (!allPhotos(slab).length) return "Hidden on the site — no photo yet";
  return "";
};

export const WebsiteFlags = ({ slab, prefix, onSaved, className = "" }) => {
  const [busy, setBusy] = useState("");
  const flagged = slab.new_arrival || slab.on_sale;
  const why = flagged ? blocker(slab) : "";

  const save = async (key) => {
    setBusy(key);
    try {
      const { data } = await api.patch(`/slabs/${slab.id}/flags`, {
        [key]: !slab[key],
      });
      onSaved?.(data);
      toast.success(
        data[key]
          ? `${slab.slab_color || "Slab"} is now ${
              key === "on_sale"
                ? "on sale"
                : key === "showroom_featured"
                  ? "on the showroom screen"
                  : "a new arrival"
            }`
          : "Taken off that feature",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save that",
      );
    }
    setBusy("");
  };

  return (
    <div className={className} data-testid={`${prefix}-flags`}>
      <div className="flex gap-2">
        {FLAGS.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            data-testid={`${prefix}-flag-${key}`}
            disabled={busy === key}
            onClick={() => save(key)}
            className={`flex h-9 flex-1 items-center justify-center gap-1.5 rounded-md border text-[10px] font-bold uppercase tracking-widest transition-colors duration-150 disabled:opacity-50 ${
              slab[key]
                ? "border-emerald-500/50 bg-emerald-500/15 text-emerald-300"
                : "border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-200"
            }`}
          >
            <Icon size={12} weight="bold" /> {label}
          </button>
        ))}
      </div>
      {why && (
        <p
          className="mt-1.5 text-[10px] uppercase tracking-widest text-amber-400"
          data-testid={`${prefix}-flag-warning`}
        >
          {why}
        </p>
      )}
    </div>
  );
};
